import React from 'react';
import { Link } from 'react-router-dom';

interface ClimateRiskData {
  address: string;
  timestamp: string;
  riskScores: {
    flood: number;
    wildfire: number;
    heat: number;
    hurricane: number;
    earthquake: number;
    tornado: number;
    landslide: number;
    drought: number;
  };
  overallRisk: number;
  dataSource: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

interface ClimateRiskReportProps {
  data: ClimateRiskData;
}

const ClimateRiskReport: React.FC<ClimateRiskReportProps> = ({ data }) => {
  const getRiskColor = (score: number): string => {
    if (score >= 80) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 20) return 'text-blue-600 bg-blue-50 border-blue-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getRiskLevel = (score: number): string => {
    if (score >= 80) return 'Very High';
    if (score >= 60) return 'High';
    if (score >= 40) return 'Moderate';
    if (score >= 20) return 'Low';
    return 'Very Low';
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Report Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold mb-2">Climate Risk Assessment Report</h2>
            <p className="text-blue-100">{data.address}</p>
            <p className="text-blue-200 text-sm">Generated on {new Date(data.timestamp).toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{data.overallRisk}</div>
            <div className="text-sm">Overall Risk Score</div>
          </div>
        </div>
      </div>

      {/* Risk Scores Grid */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment by Category</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(data.riskScores).map(([riskType, score]) => (
            <div key={riskType} className={`p-4 rounded-lg border ${getRiskColor(score)}`}>
              <div className="text-sm font-medium capitalize">{riskType.replace('_', ' ')}</div>
              <div className="text-2xl font-bold mt-1">{score}</div>
              <div className="text-xs mt-1">{getRiskLevel(score)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Insights */}
      <div className="p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
        <div className="space-y-3">
          {data.riskScores.flood >= 60 && (
            <div className="flex items-start p-3 bg-blue-50 border border-blue-200 rounded-md">
              <span className="text-blue-600 mr-3">💧</span>
              <div>
                <div className="font-medium text-blue-900">Flood Risk Alert</div>
                <div className="text-sm text-blue-700">This property has elevated flood risk. Consider flood insurance and mitigation measures.</div>
              </div>
            </div>
          )}
          {data.riskScores.wildfire >= 60 && (
            <div className="flex items-start p-3 bg-orange-50 border border-orange-200 rounded-md">
              <span className="text-orange-600 mr-3">🔥</span>
              <div>
                <div className="font-medium text-orange-900">Wildfire Risk Alert</div>
                <div className="text-sm text-orange-700">This area has significant wildfire risk. Implement defensible space and fire-resistant landscaping.</div>
              </div>
            </div>
          )}
          {data.riskScores.heat >= 70 && (
            <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-md">
              <span className="text-red-600 mr-3">🌡️</span>
              <div>
                <div className="font-medium text-red-900">Extreme Heat Risk</div>
                <div className="text-sm text-red-700">Prepare for extreme heat events with cooling systems and emergency plans.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Property Protection Recommendations */}
      <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 border-t border-green-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          🛡️ Property Protection Recommendations
        </h3>
        <div className="space-y-3">
          <div className="bg-white border border-green-200 rounded-lg p-4">
            <div className="text-sm text-gray-700 space-y-2">
              <p><strong>🏠 Insurance Review:</strong> Share this report with your insurance agent to ensure adequate coverage.</p>
              <p><strong>🔧 Mitigation Measures:</strong> Consider risk-specific improvements to reduce exposure.</p>
              <p><strong>📋 Emergency Planning:</strong> Develop family emergency plans based on your area's primary risks.</p>
              <p><strong>💰 Property Value:</strong> Climate risks can impact property values - stay informed for investment decisions.</p>
              <p><strong>📞 Emergency Contacts:</strong> US Emergency: 911 • Local Emergency Management: Contact your county office</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources */}
      <div className="p-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Sources & Methodology</h3>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>Primary Sources:</strong> {data.dataSource}</p>
            <p><strong>Coordinates:</strong> {data.location.latitude.toFixed(4)}, {data.location.longitude.toFixed(4)}</p>
            <p><strong>Assessment Method:</strong> Multi-source climate risk aggregation following FEMA National Risk Index methodology</p>
            <p><strong>Data Freshness:</strong> Current as of {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Report generated by Seawater Climate Risk Platform
        </div>
        <div className="space-x-3">
          <button
            onClick={handleDownloadPDF}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Download PDF
          </button>
          <Link
            to="/assessment"
            className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            New Assessment
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClimateRiskReport;